import { IKinship, determineKinships } from '@/common/constants/kinship';
import { MARKET, TMarket } from '@/common/constants/markets';
import { fileSchema } from '@/common/utils/zod';
import { IGenderOption } from '@coverflex-tech/hypernova-utils';
import { endOfToday } from 'date-fns';
import { t } from 'i18next';
import { z } from 'zod';

export const familyMemberFormSchema = ({
  market,
  hasSpouse,
  kinshipVerificationRequired,
}: TFamilyMemberFormSchemaArgs) => {
  const stringSchema = z.string().min(1, t('errors.input.required'));
  const shortNameSchema = z
    .string()
    .max(11, t('dashboard.add_family_member.errors.invalid_length'));
  const birthdayDateSchema = z
    .date({
      required_error: t('errors.input.invalid_date'),
      invalid_type_error: t('errors.input.invalid_date'),
    })
    .max(endOfToday(), t('errors.input.invalid_date'));
  const genderSchema = z.custom<IGenderOption>(
    (option) => ['M', 'F'].includes((option as IGenderOption)?.value),
    t('errors.input.required')
  );
  const kinshipSchema = z
    .custom<IKinship>(Boolean, t('errors.input.required'))
    .superRefine((kinship, ctx) => {
      if (!determineKinships(market).some((k) => k.value === kinship?.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('errors.input.required'),
        });
      }

      if (hasSpouse && kinship?.value === 'spouse') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('dashboard.add_family_member.errors.already_has_spouse'),
        });
      }

      return kinship;
    });
  const dependantSchema = z.custom<'dependant' | 'not_dependant' | ''>(
    (d) => ['dependant', 'not_dependant', ''].includes(d as string),
    t('errors.input.required')
  );

  const filesSchema = kinshipVerificationRequired
    ? z.array(fileSchema).min(1, t('errors.input.required'))
    : z.array(fileSchema);

  if (market === MARKET.IT) {
    return z.object({
      fullName: stringSchema,
      taxId: stringSchema,
      birthdayDate: birthdayDateSchema,
      shortName: shortNameSchema.optional(),
      kinship: kinshipSchema,
      gender: genderSchema.optional(),
      dependant: dependantSchema,
      nonSufficient: stringSchema,
      certification: z.literal<boolean>(true),
      files: filesSchema,
    });
  }

  if (market === MARKET.ES) {
    return z
      .object({
        fullName: stringSchema,
        taxId: z.string().optional(),
        birthdayDate: birthdayDateSchema,
        shortName: shortNameSchema.optional(),
        kinship: kinshipSchema,
        gender: genderSchema.optional(),
        dependant: dependantSchema.optional(),
        nonSufficient: z.string().optional(),
        certification: z.boolean().optional(),
        files: filesSchema,
      })
      .superRefine(({ kinship, taxId }, ctx) => {
        const isTaxIdValid = Boolean(taxId || kinship.value === 'child');
        if (!isTaxIdValid) {
          ctx.addIssue({
            path: ['taxId'],
            code: z.ZodIssueCode.custom,
            message: t('errors.input.required'),
          });
        }
        return isTaxIdValid;
      });
  }

  return z.object({
    fullName: stringSchema,
    taxId: stringSchema,
    birthdayDate: birthdayDateSchema,
    shortName: shortNameSchema.optional(),
    kinship: kinshipSchema,
    gender: genderSchema,
    dependant: dependantSchema.optional(),
    nonSufficient: z.string().optional(),
    certification: z.boolean().optional(),
    files: filesSchema,
  });
};

type TFamilyMemberFormSchemaArgs = {
  market: TMarket;
  hasSpouse: boolean;
  kinshipVerificationRequired: boolean;
};

export type TAddFamilyMemberFormData = z.infer<
  ReturnType<typeof familyMemberFormSchema>
>;
